import { useMemo, useState } from 'react';
import { Button, Checkbox, Input, Modal, Textarea } from '@vwfs-bronson/bronson-react';
import { Agent, CreateScheduleRequest, Schedule, Team } from '../../fes';
import { formatInTimeZone, previewCron } from './cronPreview';

const defaultTimezone = 'Europe/Berlin';
const defaultCron = '0 6 * * *';

export type ScheduleFormValues = {
  name: string;
  description: string;
  cron: string;
  timezone: string;
  agentId: string;
  prompt: string;
  skillIds: string[];
  concurrencyPolicy: 'skip' | 'allow';
  enabled: boolean;
  teamId: string;
};

function toFormValues(schedule: Schedule | null): ScheduleFormValues {
  return {
    name: schedule?.name ?? '',
    description: schedule?.description ?? '',
    cron: schedule?.trigger?.cron ?? defaultCron,
    timezone: schedule?.trigger?.timezone ?? defaultTimezone,
    agentId: schedule?.jobTemplate?.agentId ?? '',
    prompt: schedule?.jobTemplate?.prompt ?? '',
    skillIds: schedule?.jobTemplate?.context?.skills ?? [],
    concurrencyPolicy: schedule?.concurrencyPolicy ?? 'skip',
    enabled: schedule?.enabled ?? true,
    teamId: schedule?.teamId ?? '',
  };
}

export function toCreateRequest(values: ScheduleFormValues): CreateScheduleRequest {
  const context = {
    ...(values.skillIds.length ? { skills: values.skillIds } : {}),
  };

  return {
    name: values.name.trim(),
    ...(values.description.trim() ? { description: values.description.trim() } : {}),
    enabled: values.enabled,
    trigger: { cron: values.cron.trim(), timezone: values.timezone.trim() },
    concurrencyPolicy: values.concurrencyPolicy,
    jobTemplate: {
      agentId: values.agentId,
      ...(values.prompt.trim() ? { prompt: values.prompt.trim() } : {}),
      ...(Object.keys(context).length ? { context } : {}),
    },
  };
}

type ScheduleFormPanelProps = {
  schedule: Schedule | null;
  agents: Agent[];
  teams: Team[];
  saveError?: string;
  isSaving: boolean;
  onSave: (values: ScheduleFormValues) => Promise<void>;
  onClose: () => void;
};

export function ScheduleFormPanel({
  schedule,
  agents,
  teams,
  saveError,
  isSaving,
  onSave,
  onClose,
}: ScheduleFormPanelProps) {
  const [values, setValues] = useState<ScheduleFormValues>(() => toFormValues(schedule));

  const set = <K extends keyof ScheduleFormValues>(key: K, value: ScheduleFormValues[K]) =>
    setValues(current => ({ ...current, [key]: value }));

  // The single most useful thing this form can show: what the expression the
  // user just typed actually means, in their own timezone.
  const preview = useMemo(
    () => previewCron(values.cron, values.timezone),
    [values.cron, values.timezone]
  );

  const nameMissing = values.name.trim() === '';
  const agentMissing = values.agentId === '';
  // The owning team is chosen once and fixed thereafter.
  const teamMissing = !schedule && values.teamId === '';
  const canSave = !isSaving && !nameMissing && !agentMissing && !teamMissing && preview.ok;

  return (
    <Modal
      buttonCloseLabel="Close schedule form"
      onClose={onClose}
      shown
      title={schedule ? `Edit ${schedule.name}` : 'New schedule'}
    >
      {saveError ? (
        <div className="app-result app-team-error" role="alert">
          {saveError}
        </div>
      ) : null}

      <div className="app-detail-grid">
        <label className="app-form-field app-form-field--wide" htmlFor="schedule-name">
          <span>Name</span>
          <Input
            id="schedule-name"
            value={values.name}
            onChange={event => set('name', event.target.value)}
            placeholder="Nightly dependency audit"
          />
        </label>

        <label className="app-form-field app-form-field--wide" htmlFor="schedule-description">
          <span>Description</span>
          <Input
            id="schedule-description"
            value={values.description}
            onChange={event => set('description', event.target.value)}
            placeholder="What this run is for"
          />
        </label>

        <label className="app-form-field" htmlFor="schedule-cron">
          <span>Cron expression</span>
          <Input
            id="schedule-cron"
            value={values.cron}
            onChange={event => set('cron', event.target.value)}
            placeholder="0 6 * * *"
          />
        </label>

        <label className="app-form-field" htmlFor="schedule-timezone">
          <span>Timezone</span>
          <Input
            id="schedule-timezone"
            value={values.timezone}
            onChange={event => set('timezone', event.target.value)}
            placeholder="Europe/Berlin"
          />
        </label>

        <div className="app-form-field app-form-field--wide" data-testid="schedule-cron-preview">
          <span>Next runs</span>
          {preview.ok ? (
            <ul className="app-schedule-preview">
              {preview.runs.map(run => (
                <li key={run.toISOString()}>{formatInTimeZone(run, values.timezone)}</li>
              ))}
            </ul>
          ) : (
            <p className="app-team-error" role="alert">
              {preview.error}
            </p>
          )}
        </div>

          <label className="app-form-field" htmlFor="schedule-team">
            <span>Team</span>
            {schedule ? (
              <Input id="schedule-team" value={schedule.teamId ?? ''} readOnly />
            ) : (
              <select
                className="c-input__input"
                id="schedule-team"
                value={values.teamId}
                onChange={event => set('teamId', event.target.value)}
              >
                <option value="">Select a team</option>
                {teams.map(team => (
                  <option key={team.teamId} value={team.teamId}>
                    {team.name || team.teamId}
                  </option>
                ))}
              </select>
            )}
          </label>

          <label className="app-form-field" htmlFor="schedule-agent">
            <span>Agent</span>
            <select
              className="c-input__input"
              id="schedule-agent"
              value={values.agentId}
              onChange={event => set('agentId', event.target.value)}
            >
              <option value="">Select an agent</option>
              {agents.map(agent => (
                <option key={agent.agentId} value={agent.agentId}>
                  {agent.name}
                </option>
              ))}
            </select>
          </label>

          <label className="app-form-field" htmlFor="schedule-concurrency">
            <span>If the previous run is still going</span>
            <select
              className="c-input__input"
              id="schedule-concurrency"
              value={values.concurrencyPolicy}
              onChange={event => set('concurrencyPolicy', event.target.value as 'skip' | 'allow')}
            >
              <option value="skip">Skip this run</option>
              <option value="allow">Start another run</option>
            </select>
          </label>

          <div className="app-schedule-enabled-field">
            <span className="app-schedule-enabled-label">Status</span>
            <Checkbox
              id="schedule-enabled"
              checked={values.enabled}
              onChange={event => set('enabled', event.target.checked)}
            >
              Active schedule
            </Checkbox>
            <span className="app-field-hint">Inactive schedules remain saved but do not start new runs.</span>
          </div>

          <label className="app-form-field app-form-field--wide" htmlFor="schedule-prompt">
            <span>Prompt</span>
            <Textarea
              id="schedule-prompt"
              value={values.prompt}
              onChange={event => set('prompt', event.target.value)}
              placeholder="What should the agent do on every run? Name any repositories it should work on."
              rows={4}
            />
          </label>
      </div>

      <div className="app-actions app-schedule-form-actions">
        <Button secondary onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button disabled={!canSave} onClick={() => onSave(values)}>
          {isSaving ? 'Saving...' : 'Save schedule'}
        </Button>
      </div>
    </Modal>
  );
}
